package com.wintermar.inspection

import app.cash.sqldelight.Query
import app.cash.sqldelight.TransacterImpl
import app.cash.sqldelight.db.QueryResult
import app.cash.sqldelight.db.SqlCursor
import app.cash.sqldelight.db.SqlDriver
import kotlin.Any
import kotlin.Boolean
import kotlin.Long
import kotlin.String
import migrations.TemplateTable

public class TemplateTableQueries(
  driver: SqlDriver,
) : TransacterImpl(driver) {
  public fun <T : Any> getAllTemplates(mapper: (
    id: String,
    name: String,
    version: Long?,
    publishedDate: String,
    numberOfPages: Long?,
    numberOfSections: Long?,
    numberOfQuestions: Long?,
    status: String,
  ) -> T): Query<T> = Query(460_222_320, arrayOf("TemplateTable"), driver, "TemplateTable.sq", "getAllTemplates", "SELECT TemplateTable.id, TemplateTable.name, TemplateTable.version, TemplateTable.publishedDate, TemplateTable.numberOfPages, TemplateTable.numberOfSections, TemplateTable.numberOfQuestions, TemplateTable.status FROM TemplateTable") { cursor ->
    mapper(
      cursor.getString(0)!!,
      cursor.getString(1)!!,
      cursor.getLong(2),
      cursor.getString(3)!!,
      cursor.getLong(4),
      cursor.getLong(5),
      cursor.getLong(6),
      cursor.getString(7)!!
    )
  }

  public fun getAllTemplates(): Query<TemplateTable> = getAllTemplates(::TemplateTable)

  public fun <T : Any> getTemplateById(id: String, mapper: (
    id: String,
    name: String,
    version: Long?,
    publishedDate: String,
    numberOfPages: Long?,
    numberOfSections: Long?,
    numberOfQuestions: Long?,
    status: String,
  ) -> T): Query<T> = GetTemplateByIdQuery(id) { cursor ->
    mapper(
      cursor.getString(0)!!,
      cursor.getString(1)!!,
      cursor.getLong(2),
      cursor.getString(3)!!,
      cursor.getLong(4),
      cursor.getLong(5),
      cursor.getLong(6),
      cursor.getString(7)!!
    )
  }

  public fun getTemplateById(id: String): Query<TemplateTable> = getTemplateById(id, ::TemplateTable)

  public fun <T : Any> getFullTemplate(id: String, mapper: (
    template_id: String,
    template_name: String,
    version: Long?,
    publishedDate: String,
    numberOfPages: Long?,
    numberOfSections: Long?,
    numberOfQuestions: Long?,
    page_id: String?,
    page_title: String?,
    page_pos: Long?,
    section_id: String?,
    section_title: String?,
    section_pos: Long?,
    q_id: String?,
    questionText: String?,
    guidelines: String?,
    required: Boolean?,
    q_pos: Long?,
    answerType: String?,
    failedOptionId: String?,
    neutralOptionId: String?,
  ) -> T): Query<T> = GetFullTemplateQuery(id) { cursor ->
    mapper(
      cursor.getString(0)!!,
      cursor.getString(1)!!,
      cursor.getLong(2),
      cursor.getString(3)!!,
      cursor.getLong(4),
      cursor.getLong(5),
      cursor.getLong(6),
      cursor.getString(7),
      cursor.getString(8),
      cursor.getLong(9),
      cursor.getString(10),
      cursor.getString(11),
      cursor.getLong(12),
      cursor.getString(13),
      cursor.getString(14),
      cursor.getString(15),
      cursor.getBoolean(16),
      cursor.getLong(17),
      cursor.getString(18),
      cursor.getString(19),
      cursor.getString(20)
    )
  }

  public fun getFullTemplate(id: String): Query<GetFullTemplate> = getFullTemplate(id, ::GetFullTemplate)

  public fun <T : Any> getTemplatesPaginated(
    limit: Long,
    offset: Long,
    mapper: (
      id: String,
      name: String,
      version: Long?,
      publishedDate: String,
      numberOfPages: Long?,
      numberOfSections: Long?,
      numberOfQuestions: Long?,
      status: String,
    ) -> T,
  ): Query<T> = GetTemplatesPaginatedQuery(limit, offset) { cursor ->
    mapper(
      cursor.getString(0)!!,
      cursor.getString(1)!!,
      cursor.getLong(2),
      cursor.getString(3)!!,
      cursor.getLong(4),
      cursor.getLong(5),
      cursor.getLong(6),
      cursor.getString(7)!!
    )
  }

  public fun getTemplatesPaginated(limit: Long, offset: Long): Query<TemplateTable> = getTemplatesPaginated(limit, offset, ::TemplateTable)

  public fun countTemplates(): Query<Long> = Query(-1_490_169_784, arrayOf("TemplateTable"), driver, "TemplateTable.sq", "countTemplates", "SELECT count(*) FROM TemplateTable") { cursor ->
    cursor.getLong(0)!!
  }

  /**
   * @return The number of rows updated.
   */
  public fun insertTemplate(
    id: String,
    name: String,
    version: Long?,
    publishedDate: String,
    numberOfPages: Long?,
    numberOfSections: Long?,
    numberOfQuestions: Long?,
    status: String,
  ): QueryResult<Long> {
    val result = driver.execute(-966_547_535, """
        |INSERT INTO TemplateTable(id, name, version, publishedDate, numberOfPages, numberOfSections, numberOfQuestions, status)
        |VALUES (?, ?, ?, ?, ?, ?, ?, ?)
        |ON CONFLICT(id) DO UPDATE SET
        |   name=excluded.name,
        |   version=excluded.version,
        |   publishedDate=excluded.publishedDate,
        |   numberOfPages=excluded.numberOfPages,
        |   numberOfSections=excluded.numberOfSections,
        |   numberOfQuestions=excluded.numberOfQuestions,
        |   status=excluded.status
        """.trimMargin(), 8) {
          var parameterIndex = 0
          bindString(parameterIndex++, id)
          bindString(parameterIndex++, name)
          bindLong(parameterIndex++, version)
          bindString(parameterIndex++, publishedDate)
          bindLong(parameterIndex++, numberOfPages)
          bindLong(parameterIndex++, numberOfSections)
          bindLong(parameterIndex++, numberOfQuestions)
          bindString(parameterIndex++, status)
        }
    notifyQueries(-966_547_535) { emit ->
      emit("TemplateTable")
    }
    return result
  }

  /**
   * @return The number of rows updated.
   */
  public fun insertReplaceTemplate(
    id: String,
    name: String,
    version: Long?,
    publishedDate: String,
    numberOfPages: Long?,
    numberOfSections: Long?,
    numberOfQuestions: Long?,
    status: String,
  ): QueryResult<Long> {
    val result = driver.execute(272_193_591, """
        |INSERT OR REPLACE INTO TemplateTable(id, name, version, publishedDate, numberOfPages, numberOfSections, numberOfQuestions, status)
        |VALUES (?, ?, ?, ?, ?, ?, ?,?)
        """.trimMargin(), 8) {
          var parameterIndex = 0
          bindString(parameterIndex++, id)
          bindString(parameterIndex++, name)
          bindLong(parameterIndex++, version)
          bindString(parameterIndex++, publishedDate)
          bindLong(parameterIndex++, numberOfPages)
          bindLong(parameterIndex++, numberOfSections)
          bindLong(parameterIndex++, numberOfQuestions)
          bindString(parameterIndex++, status)
        }
    notifyQueries(272_193_591) { emit ->
      emit("TemplateTable")
    }
    return result
  }

  /**
   * @return The number of rows updated.
   */
  public fun deleteTemplateById(id: String): QueryResult<Long> {
    val result = driver.execute(173_350_677, """DELETE FROM TemplateTable WHERE id = ?""", 1) {
          var parameterIndex = 0
          bindString(parameterIndex++, id)
        }
    notifyQueries(173_350_677) { emit ->
      emit("TemplateTable")
    }
    return result
  }

  /**
   * @return The number of rows updated.
   */
  public fun deleteAllTemplates(): QueryResult<Long> {
    val result = driver.execute(-69_140_927, """DELETE FROM TemplateTable""", 0)
    notifyQueries(-69_140_927) { emit ->
      emit("TemplateTable")
    }
    return result
  }

  private inner class GetTemplateByIdQuery<out T : Any>(
    public val id: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("TemplateTable", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("TemplateTable", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> = driver.executeQuery(702_713_924, """SELECT TemplateTable.id, TemplateTable.name, TemplateTable.version, TemplateTable.publishedDate, TemplateTable.numberOfPages, TemplateTable.numberOfSections, TemplateTable.numberOfQuestions, TemplateTable.status FROM TemplateTable WHERE id = ?""", mapper, 1) {
      var parameterIndex = 0
      bindString(parameterIndex++, id)
    }

    override fun toString(): String = "TemplateTable.sq:getTemplateById"
  }

  private inner class GetFullTemplateQuery<out T : Any>(
    public val id: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("TemplateTable", "TemplatePageTable", "TemplatePageSectionTable", "TemplatePageSectionQuestionTable", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("TemplateTable", "TemplatePageTable", "TemplatePageSectionTable", "TemplatePageSectionQuestionTable", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> = driver.executeQuery(-766_241_535, """
    |SELECT
    |    T.id AS template_id, T.name AS template_name, T.version, T.publishedDate, T.numberOfPages, T.numberOfSections, T.numberOfQuestions,
    |    P.id AS page_id, P.title AS page_title, P.orderPosition AS page_pos,
    |    S.id AS section_id, S.title AS section_title, S.orderPosition AS section_pos,
    |    Q.id AS q_id, Q.questionText, Q.guidelines, Q.required, Q.orderPosition AS q_pos, Q.answerType, Q.failedOptionId, Q.neutralOptionId
    |FROM TemplateTable T
    |LEFT JOIN TemplatePageTable P ON T.id = P.templateId
    |LEFT JOIN TemplatePageSectionTable S ON P.id = S.pageId
    |LEFT JOIN TemplatePageSectionQuestionTable Q ON S.id = Q.sectionId
    |WHERE T.id = ?
    |ORDER BY page_pos, section_pos, q_pos
    """.trimMargin(), mapper, 1) {
      var parameterIndex = 0
      bindString(parameterIndex++, id)
    }

    override fun toString(): String = "TemplateTable.sq:getFullTemplate"
  }

  private inner class GetTemplatesPaginatedQuery<out T : Any>(
    public val limit: Long,
    public val offset: Long,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("TemplateTable", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("TemplateTable", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> = driver.executeQuery(1_596_907_116, """
    |SELECT TemplateTable.id, TemplateTable.name, TemplateTable.version, TemplateTable.publishedDate, TemplateTable.numberOfPages, TemplateTable.numberOfSections, TemplateTable.numberOfQuestions, TemplateTable.status FROM TemplateTable
    |LIMIT ? OFFSET ?
    """.trimMargin(), mapper, 2) {
      var parameterIndex = 0
      bindLong(parameterIndex++, limit)
      bindLong(parameterIndex++, offset)
    }

    override fun toString(): String = "TemplateTable.sq:getTemplatesPaginated"
  }
}
